import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaRegHeart,
  FaHandHoldingHeart,
  FaCheck,
  FaUsers,
  FaChalkboardTeacher,
  FaWheelchair,
  FaBookOpen,
  FaDesktop,
  FaGraduationCap,
} from "react-icons/fa";
import "./InclusiveEducation.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const InclusiveEducation = () => {
  const initiatives = [
    {
      icon: FaChalkboardTeacher,
      title: "Training & Capacity Building",
      text: "Training for Teachers, Parents, and School Management Committees.",
    },
    {
      icon: FaWheelchair,
      title: "Assistive Support",
      text: "Providing access to assistive devices and study materials.",
    },
    {
      icon: FaBookOpen,
      title: "Learning Assessments",
      text: "Assessing children with specific learning disabilities.",
    },
    {
      icon: FaDesktop,
      title: "Academic Support",
      text: "Offering technical and professional support for academic improvement.",
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* ================= Hero (rebuilt to 1521 x 482, matching your other pages) ================= */}
      <section className="ie-page">
        <div className="ie-container">
          <motion.div
            className="ie-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="ie-hero__image"
              style={{ backgroundImage: "url(/assets/images/backgrounds/Inclusive-Education.png)" }}
            />
            <div className="ie-hero__overlay" />

            <div className="ie-hero__content">
              <div className="ie-hero__mark">
                <FaRegHeart className="ie-hero__mark-heart" />
                <FaHandHoldingHeart className="ie-hero__mark-hand" />
                <svg className="ie-hero__squiggle" viewBox="0 0 220 120" fill="none">
                  <path
                    d="M2 2 C 2 40, 2 70, 40 90 S 150 118, 215 108"
                    stroke="#f2622e"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>

              <motion.h2
                className="ie-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Inclusive Education
              </motion.h2>
              <motion.p
                className="ie-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Vikash advocates for inclusive education by creating
                accessible learning environments for all children, ensuring
                equal opportunities for success.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Introduction ================= */}
      <section className="ie-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-start g-5">
              <motion.div className="col-lg-5" variants={staggerItem}>
                <span className="ie-eyebrow__dash" />
                <h2 className="ie-heading">
                  Creating an
                  <br />
                  <span>Enabling Environment</span>
                </h2>
                <p className="ie-intro__text">
                  All children deserve equal opportunities. Vikash works to
                  provide an enabling environment for children with
                  disabilities to learn and live with dignity. We facilitate
                  social interaction, provide special medical services, and
                  offer pre-schooling with Activities of Daily Living Skills
                  (ADLS), speech therapy, and behavioral orientation. We also
                  orient local school teachers on managing children with
                  special learning needs.
                </p>

                <div className="ie-callout">
                  <span className="ie-callout__icon">
                    <FaHandHoldingHeart />
                  </span>
                  <p className="ie-callout__text">
                    Empowering every child through care, understanding, and
                    equal opportunities.
                  </p>
                </div>
              </motion.div>

              <motion.div className="col-lg-7" variants={staggerItem}>
                <Row className="g-4">
                  <Col xs={6}>
                    <div className="ie-photoCard">
                      <div className="ie-photoCard__imageWrap">
                        <img
                          src="/assets/images/Subsections/ie1.jpg"
                          alt="Inclusive classroom"
                          className="ie-photoCard__image"
                        />
                      </div>
                      <div className="ie-photoCard__caption">
                        <span className="ie-photoCard__check">
                          <FaCheck />
                        </span>
                        <div>
                          <h6>Special Learning Support</h6>
                          <p>ADLS, speech therapy, behavioral orientation & more.</p>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="ie-photoCard">
                      <div className="ie-photoCard__imageWrap">
                        <img
                          src="/assets/images/Subsections/ie2.jpg"
                          alt="Teacher supporting child with special needs"
                          className="ie-photoCard__image"
                        />
                      </div>
                      <div className="ie-photoCard__caption">
                        <span className="ie-photoCard__check">
                          <FaCheck />
                        </span>
                        <div>
                          <h6>Inclusive Participation</h6>
                          <p>Creating safe spaces where every child can learn and grow.</p>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= Key Initiatives ================= */}
      <section className="ie-approach">
        <span className="ie-approach__dots ie-approach__dots--left" aria-hidden="true" />
        <span className="ie-approach__leaf" aria-hidden="true">
          🌿
        </span>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="ie-approach__header"
          >
            <span className="ie-approach__badge">
              <FaUsers />
            </span>
            <h2 className="ie-heading ie-heading--center">
              Our Approach to <span>Inclusive Education</span>
            </h2>
            <span className="ie-heading__underline" />
            <p className="ie-approach__lead">
              We empower the entire educational ecosystem to support children
              with disabilities.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item) => {
                const Icon = item.icon;
                return (
                  <Col md={6} key={item.title}>
                    <motion.div variants={staggerItem} className="ie-card">
                      <span className="ie-card__icon">
                        <Icon />
                      </span>
                      <div>
                        <h5 className="ie-card__title">{item.title}</h5>
                        <p className="ie-card__text">{item.text}</p>
                      </div>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="ie-cta">
        <Container>
          <motion.div
            className="ie-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="ie-cta__content">
              <span className="ie-cta__icon">
                <FaHandHoldingHeart />
              </span>
              <h2 className="ie-cta__title">
                Ensure Every
                <br />
                <span>Child Can Learn</span>
              </h2>
              <span className="ie-cta__underline" />
              <p className="ie-cta__text">
                Your contribution helps provide assistive devices, specialized
                training, and support to create truly inclusive schools.
              </p>
              <div className="ie-cta__actions">
                <button
                  type="button"
                  className="ie-btn ie-btn--solid"
                  onClick={openDonation}
                >
                  <FaRegHeart /> Donate
                </button>
                <button
                  type="button"
                  className="ie-btn ie-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>

            <div className="ie-cta__imageWrap">
              <img
                src="/assets/images/Subsections/ie1.jpg"
                alt="Children smiling"
                className="ie-cta__image"
              />
              <span className="ie-cta__badge">
                <FaGraduationCap />
              </span>
              <span className="ie-cta__dots" />
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default InclusiveEducation;